import React, { useContext, useEffect, useState } from "react";
import {
  Card,
  CardContent,
  CardHeader,
  CircularProgress,
  Container,
  Grid,
  IconButton,
  List,
  ListItem,
  ListItemText,
  Typography,
} from "@material-ui/core";
import ArrowBackIcon from "@material-ui/icons/ArrowBack";
import CheckCircleIcon from "@material-ui/icons/CheckCircle";
import SaveIcon from "@material-ui/icons/Save";
import { IDropdownOption, TextField } from "@fluentui/react";
import { useHistory, useParams } from "react-router";
import { getApiUrl } from "../../api-clients";
import {
  ApiException,
  ExamApi,
  ExamDto,
  ExamGradeApi,
  ExamGradeDto,
  StudentApi,
  StudentView,
} from "../../api-clients/api";
import { AppContext } from "../../contexts/AppContext";
import Loading from "../Shared/Loading";
import NoResult from "../Shared/NoResult";
import TeachersDropdown from "../Shared/TeachersDropdown";
import LabelFieldValue from "../Shared/LabelFieldValue";
import { dateToString } from "../../helper";
import { useTranslation } from "react-i18next";
import { useRole } from "../../hooks/useRole";

export default function Exam() {
  const { t } = useTranslation();
  const { isAdmin, isTeacher } = useRole();
  const { openSnackbar } = useContext(AppContext);
  const { examId }: any = useParams();
  const history = useHistory();
  const [examApi] = useState<ExamApi>(new ExamApi(getApiUrl()));
  const [studentApi] = useState<StudentApi>(new StudentApi(getApiUrl()));
  const [examGradeApi] = useState<ExamGradeApi>(new ExamGradeApi(getApiUrl()));

  const [exam, setExam] = useState<ExamDto | null>(null);
  const [examLoaded, setExamLoaded] = useState<boolean>(false);
  const [students, setStudents] = useState<StudentView[]>([]);
  const [studentsLoading, setStudentsLoading] = useState<boolean>(true);

  const [teacherId, setTeacherId] = useState<string | undefined>(undefined);

  const [gradeValues, setGradeValues] = useState<Record<string, string>>({});
  const [gradeTexts, setGradeTexts] = useState<Record<string, string>>({});
  const [comments, setComments] = useState<Record<string, string>>({});
  const [examGradeIds, setExamGradeIds] = useState<Record<string, string>>({});
  const [gradesLoading, setGradesLoading] = useState<boolean>(true);
  const [saving, setSaving] = useState<Record<string, boolean>>({});
  const [saved, setSaved] = useState<Record<string, boolean>>({});

  const canGrade = isAdmin || isTeacher;

  useEffect(() => {
    setExamLoaded(false);
    examApi
      .getById(examId)
      .then((res) => {
        setExam(res);
        setExamLoaded(true);
        setTeacherId(res?.teacherId);
      })
      .catch(() => setExamLoaded(true));
  }, [examApi, examId]);

  useEffect(() => {
    if (exam?.classroomId == null || exam?.accademicYearId == null) return;
    setStudentsLoading(true);
    studentApi
      .classroom(exam.classroomId, exam.accademicYearId)
      .then((res) => {
        setStudents(res);
        setStudentsLoading(false);
      })
      .catch(() => setStudentsLoading(false));
  }, [studentApi, exam]);

  // Load already-entered grades for this exam so the form opens pre-filled
  // instead of blank, and so re-saving a row updates rather than duplicates.
  useEffect(() => {
    if (!canGrade) {
      setGradesLoading(false);
      return;
    }
    setGradesLoading(true);
    examGradeApi
      .exam(examId)
      .then((res) => {
        const values: Record<string, string> = {};
        const texts: Record<string, string> = {};
        const notes: Record<string, string> = {};
        const ids: Record<string, string> = {};
        const savedFlags: Record<string, boolean> = {};
        (res ?? []).forEach((g) => {
          if (g.studentPathId == null) return;
          values[g.studentPathId] = g.gradeValue != null ? String(g.gradeValue) : "";
          texts[g.studentPathId] = g.gradeText ?? "";
          notes[g.studentPathId] = g.comment ?? "";
          if (g.examGradeId != null) ids[g.studentPathId] = g.examGradeId;
          savedFlags[g.studentPathId] = true;
        });
        setGradeValues(values);
        setGradeTexts(texts);
        setComments(notes);
        setExamGradeIds(ids);
        setSaved(savedFlags);
        setGradesLoading(false);
      })
      .catch(() => setGradesLoading(false));
  }, [examGradeApi, examId, canGrade]);

  const canSaveFor = (studentPathId: string): boolean =>
    teacherId != null &&
    (gradeValues[studentPathId]?.trim().length ?? 0) > 0 &&
    !isNaN(Number(gradeValues[studentPathId])) &&
    Number(gradeValues[studentPathId]) >= 0 &&
    Number(gradeValues[studentPathId]) <= 20;

  const saveGrade = (student: StudentView) => {
    if (
      student.studentPathId == null ||
      exam == null ||
      !canSaveFor(student.studentPathId)
    )
      return;
    const studentPathId = student.studentPathId;
    const existingExamGradeId = examGradeIds[studentPathId];
    setSaving({ ...saving, [studentPathId]: true });

    const payload = {
      examGradeId: existingExamGradeId,
      examId: exam.examId,
      studentPathId: studentPathId,
      teacherId: teacherId,
      date: exam.date,
      gradeValue: Number(gradeValues[studentPathId]),
      gradeText: gradeTexts[studentPathId] ?? "",
      comment: comments[studentPathId] ?? "",
    } as ExamGradeDto;

    const request = existingExamGradeId
      ? examGradeApi.update(payload)
      : examGradeApi.create(payload);

    request
      .then((res) => {
        setSaving({ ...saving, [studentPathId]: false });
        setSaved({ ...saved, [studentPathId]: true });
        if (!existingExamGradeId && res?.examGradeId != null) {
          setExamGradeIds({
            ...examGradeIds,
            [studentPathId]: res.examGradeId,
          });
        }
        openSnackbar(t("examGrades.saveSuccess"), "success");
      })
      .catch((err) => {
        setSaving({ ...saving, [studentPathId]: false });
        if (ApiException.isApiException(err) && err.status === 403) {
          openSnackbar(t("exams.notYourExam"), "error");
        } else {
          openSnackbar(t("common.errorOccurred"), "error");
        }
      });
  };

  return (
    <Container maxWidth="lg">
      {examLoaded ? (
        exam != null ? (
          <>
            <Grid container spacing={1} alignItems="center">
              <Grid item>
                <IconButton
                  aria-label={t("exams.backToExams")}
                  onClick={() => history.goBack()}
                >
                  <ArrowBackIcon />
                </IconButton>
              </Grid>
              <Grid item>
                <Typography variant="h5">{exam.name}</Typography>
              </Grid>
            </Grid>

            {!canGrade ? null : (
              <Card style={{ marginTop: "16px" }}>
                <CardHeader title={t("examGrades.gradebook")} />
                <CardContent>
                  <Grid container spacing={2}>
                    <Grid item sm={4} xs={12}>
                      <LabelFieldValue
                        label={t("exams.date")}
                        value={dateToString(exam.date)}
                      />
                    </Grid>
                    <Grid item sm={4} xs={12}>
                      {isAdmin ? (
                        <TeachersDropdown
                          required
                          label={t("classSchedule.teacher")}
                          placeholder={t("classSchedule.selectTeacher")}
                          selectedKey={teacherId}
                          onChange={(
                            event: React.FormEvent<HTMLDivElement>,
                            option?: IDropdownOption
                          ) => setTeacherId(option?.key as string)}
                        />
                      ) : (
                        <LabelFieldValue
                          label={t("classSchedule.teacher")}
                          value={exam.teacherName}
                        />
                      )}
                    </Grid>
                    <Grid item sm={4} xs={12}>
                      <LabelFieldValue
                        label={t("classSchedule.subject")}
                        value={exam.subjectName}
                      />
                    </Grid>
                  </Grid>

                  {!studentsLoading && !gradesLoading ? (
                    students.length > 0 ? (
                      <List>
                        {students.map((s) => {
                          const studentPathId = s.studentPathId ?? "";
                          return (
                            <ListItem key={s.studentId} divider>
                              <ListItemText
                                primary={`${s.firstName ?? ""} ${
                                  s.lastName ?? ""
                                }`}
                              />
                              <Grid
                                container
                                spacing={1}
                                alignItems="center"
                                style={{ maxWidth: "520px" }}
                              >
                                <Grid item xs={3}>
                                  <TextField
                                    label={t("examGrades.grade")}
                                    placeholder="0-20"
                                    type="number"
                                    min="0"
                                    max="20"
                                    value={gradeValues[studentPathId] ?? ""}
                                    onChange={(
                                      event: React.FormEvent<
                                        HTMLInputElement | HTMLTextAreaElement
                                      >,
                                      newValue?: string
                                    ) => {
                                      setGradeValues({
                                        ...gradeValues,
                                        [studentPathId]: newValue ?? "",
                                      });
                                      setSaved({
                                        ...saved,
                                        [studentPathId]: false,
                                      });
                                    }}
                                  />
                                </Grid>
                                <Grid item xs={4}>
                                  <TextField
                                    label={t("examGrades.gradeText")}
                                    value={gradeTexts[studentPathId] ?? ""}
                                    onChange={(
                                      event: React.FormEvent<
                                        HTMLInputElement | HTMLTextAreaElement
                                      >,
                                      newValue?: string
                                    ) => {
                                      setGradeTexts({
                                        ...gradeTexts,
                                        [studentPathId]: newValue ?? "",
                                      });
                                      setSaved({
                                        ...saved,
                                        [studentPathId]: false,
                                      });
                                    }}
                                  />
                                </Grid>
                                <Grid item xs={4}>
                                  <TextField
                                    label={t("common.comment")}
                                    value={comments[studentPathId] ?? ""}
                                    onChange={(
                                      event: React.FormEvent<
                                        HTMLInputElement | HTMLTextAreaElement
                                      >,
                                      newValue?: string
                                    ) => {
                                      setComments({
                                        ...comments,
                                        [studentPathId]: newValue ?? "",
                                      });
                                      setSaved({
                                        ...saved,
                                        [studentPathId]: false,
                                      });
                                    }}
                                  />
                                </Grid>
                                <Grid item xs={1}>
                                  {saving[studentPathId] ? (
                                    <CircularProgress size={20} />
                                  ) : saved[studentPathId] ? (
                                    <CheckCircleIcon
                                      style={{ color: "green" }}
                                    />
                                  ) : (
                                    <IconButton
                                      aria-label={t("common.save")}
                                      disabled={!canSaveFor(studentPathId)}
                                      onClick={() => saveGrade(s)}
                                    >
                                      <SaveIcon />
                                    </IconButton>
                                  )}
                                </Grid>
                              </Grid>
                            </ListItem>
                          );
                        })}
                      </List>
                    ) : (
                      <NoResult />
                    )
                  ) : (
                    <Loading />
                  )}
                </CardContent>
              </Card>
            )}
          </>
        ) : (
          <NoResult />
        )
      ) : (
        <Loading />
      )}
    </Container>
  );
}
